import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from '@gatsbyjs/reach-router';
import cn from 'classnames';

import { useOutsideClick } from '../../hooks/useOutsideClick';
import { ModalEnum } from '../../ModalEnum';
import RouteEnum from '../../RouteEnum';
import { useRouteContext } from '../../RouteProvider';
import Tracking from '../../services/Tracking';
import type { ReduxState } from '../../store';
import { openModal } from '../../store/app';
import { loadOnboarding } from '../../store/cars';
import { isSignedInSelector } from '../../store/selectors';
import Button from '../Button';
import Image from '../Image';
import Link, { type LinkProps } from '../Link';
import T, { useTranslation } from '../Translate';

import styles from './navbar.module.scss';

export default function Navbar() {
  const dispatch = useDispatch();
  const { isSignedIn, hasConsents, userName, userAvatar, myCars, messages } = useSelector((store: ReduxState) => ({
    isSignedIn: isSignedInSelector(store),
    hasConsents: store.session.hasConsents,
    userName: store.user?.data.forename
      ? `${store.user.data.forename} ${store.user.data.surname.substring(0, 1)}.`
      : undefined,
    userAvatar: store.user?.avatar_document,
    myCars: store.myCars?.selected,
    messages: store.messages?.data?.items[0]?.chat_id,
  }));
  const [mobileOpened, setMobileOpened] = useState(false);
  const [profileOpened, setProfileOpened] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { t } = useTranslation();
  const { generatePath } = useRouteContext();

  const handleOpenSignModal = () => {
    Tracking.track('NAVBAR_LOGIN_CLICKED');
    dispatch(
      openModal(ModalEnum.SIGN_IN, {
        closeCallback() {
          setProfileOpened(false);
          setMobileOpened(false);
        },
      }),
    );
    return false;
  };

  const handleGetStartedClick = () => {
    Tracking.track('NAVBAR_SIGNUP_CLICKED');
    dispatch(
      openModal(ModalEnum.SIGN_UP, {
        redirect: generatePath(RouteEnum.USER_REGISTRATION),
        closeCallback() {
          setProfileOpened(false);
          setMobileOpened(false);
        },
      }),
    );
    return false;
  };

  const handleLanguageClick = () => {
    Tracking.track('NAVBAR_GLOBE_CLICKED');
    dispatch(
      openModal(ModalEnum.REGION_SELECT, {
        closeCallback() {
          setProfileOpened(false);
          setMobileOpened(false);
        },
      }),
    );
    return false;
  };

  useOutsideClick(() => profileOpened && setProfileOpened(false), ref, [profileOpened]);

  return (
    <nav className={cn(styles.container, { opened: mobileOpened })} ref={ref}>
      <div className={styles.navbar}>
        <NavbarLogo />

        <div className={styles.wrapper} id="navbar">
          <div className={styles.mobile}>
            <div
              className={cn(styles.userNavbar, {
                opened: profileOpened,
                show: isSignedIn,
              })}
            >
              <NavLink
                disabled={!hasConsents}
                onClick={() => Tracking.track('NAVBAR_CHAT_CLICKED')}
                params={{ chat: messages }}
                to={RouteEnum.MESSAGES}
              >
                <T id="navbar.messages" />
              </NavLink>
              <NavLink
                disabled={!hasConsents}
                onClick={() => Tracking.track('NAVBAR_FAVORITES_CLICKED')}
                to={RouteEnum.FAVORITES}
              >
                <T id="navbar.favorites" />
              </NavLink>
              <NavLink
                disabled={!hasConsents}
                onClick={() => Tracking.track('NAVBAR_TRIPS_CLICKED')}
                to={RouteEnum.TRIPS}
              >
                <T id="navbar.trips" />
              </NavLink>
              <NavLink
                disabled={!hasConsents}
                onClick={() => Tracking.track('NAVBAR_PROFILE_CLICKED')}
                to={RouteEnum.MY_PROFILE}
              >
                <T id="navbar.profile" />
              </NavLink>
              <NavLink
                disabled={!hasConsents}
                onClick={() => {
                  Tracking.track('NAVBAR_MY_VEHICLES_CLICKED');
                  dispatch(loadOnboarding());
                }}
                params={{ hash: myCars }}
                to={RouteEnum.CARS}
              >
                <T id="navbar.cars" />
              </NavLink>
              <Link
                className={styles.navLink}
                onClick={() => Tracking.track('NAVBAR_LOGOUT_CLICKED')}
                to={RouteEnum.SIGN_OUT}
              >
                <T id="navbar.signOut" />
              </Link>
            </div>

            <div className={styles.publicNavbar}>
              <NavLink
                disabled={!hasConsents}
                onClick={() => Tracking.track('NAVBAR_SEARCH_CLICKED')}
                to={RouteEnum.RENT_CAR}
              >
                <T id="navbar.rentCar" />
              </NavLink>
              <NavLink onClick={() => Tracking.track('NAVBAR_OFFER_CAR_CLICKED')} to={RouteEnum.STATIC_OFFER}>
                <T id="navbar.offerCar" />
              </NavLink>
              <NavLink onClick={() => Tracking.track('NAVBAR_FAQ_CLICKED')} to={RouteEnum.STATIC_FAQ}>
                <T id="navbar.faqs" />
              </NavLink>
              <Button className={styles.globe} onClick={handleLanguageClick} title={t('navbar.globe.title')}>
                <i className="icon icon-globe" />
              </Button>
              <Link
                className={cn(styles.navbarLogin, 'button', {
                  hide: isSignedIn,
                })}
                onClick={handleOpenSignModal}
                title={t('navbar.profile.title_guest')}
                to={RouteEnum.SIGN_IN}
              >
                <T id="navbar.login" />
              </Link>
              <Link
                className={cn(styles.navbarCTA, 'button button-primary button-outline', { hide: isSignedIn })}
                onClick={handleGetStartedClick}
                to={RouteEnum.SIGN_UP}
              >
                <T id="navbar.getStarted" />
              </Link>
            </div>
          </div>

          <div className={styles.navbarRight}>
            <Button
              className={styles.navbarHamburger}
              onClick={() => setMobileOpened(!mobileOpened)}
              outline={isSignedIn}
              variant="mono"
            >
              <i className="icon icon-nav-hamburger" />
              <div className={cn(styles.userAvatar, { show: isSignedIn })}>
                {userAvatar ? <Image src={userAvatar} /> : <i className="icon icon-user-nav" />}
              </div>
            </Button>
            <Link
              className={cn(styles.navbarCTA, 'button button-primary button-outline', { hide: isSignedIn })}
              onClick={handleGetStartedClick}
              to={RouteEnum.SIGN_UP}
            >
              <T id="navbar.getStarted" />
            </Link>
            <Button
              className={cn(styles.navbarProfile, { show: isSignedIn })}
              onClick={() => setProfileOpened(!profileOpened)}
              outline
              title={t('navbar.profile.title')}
              variant="mono"
            >
              {userName}
              <div className={styles.userAvatar}>
                {userAvatar ? <Image src={userAvatar} /> : <i className="icon icon-user-nav" />}
              </div>
            </Button>
            <Button className={styles.closeBtn} onClick={() => setMobileOpened(false)}>
              <i className="icon icon-close" />
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
}

export function NavbarLogo() {
  return (
    <Link to={RouteEnum.HOMEPAGE}>
      <picture>
        <source media="(max-width: 353px)" srcSet="/img/navbar_logo_mobile.png 1x, /img/navbar_logo_mobile@2x.png 2x" />
        <img alt="HoppyGo logo" src="/img/navbar_logo.png" srcSet="/img/navbar_logo@2x.png 2x" />
      </picture>
    </Link>
  );
}

interface NavLinkProps<T> extends LinkProps<T> {
  additionalActiveClassPaths?: string[];

  setMenuOpen?(opened: boolean);
}

function NavLink<T>({ additionalActiveClassPaths = [], setMenuOpen, ...props }: NavLinkProps<T>) {
  const matches = [useMatch(`/:lang?${props.to}`)];
  for (const path of additionalActiveClassPaths) {
    matches.push(useMatch(`/:lang?${path}`));
  }
  const active = matches.some(match => match !== null);

  const handleClick = event => {
    props.onClick?.(event);
    setMenuOpen?.(false);
  };

  return <Link {...props} className={cn(styles.navLink, { active }, props.className)} onClick={handleClick} />;
}
